import { Skeleton } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';

import { deleteTaskAction, editTaskAction, getTaskAction, openTaskAction } from '../../../../redux/tasks/actions';
import {
  makeGetSubTaskOrder,
  makeGetTaskParentId,
  makeGetTaskTitle,
  makeGetTaskTitleIdentifier,
  makeIsTaskCompletedSelector,
} from '../../../../redux/tasks/taskEntities/selectors';
import ConnectedAssigneeSelect from '../task-components/ConnectedAssigneeSelect';
import TaskCalendarDropdownWrapper from '../task-components/TaskCalendarDropdownWrapper';
import TaskCardWithDropdown from '../task-components/task-heading-card/TaskCardWithDropdown';

import './style.css';
import { Tooltip } from 'antd';
import ConnectedPriorityDropdown from '../task-components/primitive-connected-task-components/ConnectedPriorityDropdown';

const SubtaskPlaceholder = () => (
  <div className="subtask-loading-placeholder-wrapper">
    <Skeleton className="ml-1" variant="circular" width={20} height={20} />
    <Skeleton className="ml-2" variant="text" height={20} width={140} />
  </div>
);

const SubTask = ({
  compressed,
  isCompleted,
  openAddSubtask,
  parentTaskId,
  subTaskId,
  subTaskOrder,
  taskTitle,
  titleIdentifier,
  tasks
}) => {
  const dispatch = useDispatch();

  const refr = useRef();

  const [titleValue, setTitleValue] = useState(taskTitle);
  const [completed, setCompleted] = useState(isCompleted);
  const [isCalendar, setIsCalendar] = useState(false);
  const [isFocused, setIsFocued] = useState(false);

  const titleId = useMemo(() => `${subTaskId}-title`, [subTaskId]);

  const newDate = new Date();
  newDate.setHours(0, 0, 0, 0);

  const setFocused = useCallback(() => setIsFocued(true), []);

  const setUnfocused = useCallback(() => setIsFocued(false), []);

  const openTicket = useCallback(
    async (e) => {
      if (e?.target?.id === titleId) {
        return;
      }
      dispatch(openTaskAction(true, titleIdentifier, subTaskId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subTaskId, titleIdentifier, titleId],
  );

  const handleDeleteSubTask = useCallback(() => {
    dispatch(deleteTaskAction(subTaskId, () => { }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTaskId]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        editTaskAction(
          {
            _id: subTaskId,
            taskTitle: titleValue,
          },
          () => {
            dispatch(getTaskAction(parentTaskId));
          },
        ),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentTaskId, subTaskId, titleValue],
  );

  const handleComplete = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(
        editTaskAction({
          _id: subTaskId,
          isCompleted: !completed,
        }),
      );
      setCompleted((completed) => !completed);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [completed, subTaskId],
  );


  const onTitleEditorChange = useCallback((e) => {
    setTitleValue(e.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTitleEditorBlur = useCallback(
    (e) => {
      if (!titleValue.trim()) {
        handleDeleteSubTask();
        return;
      }
      if (taskTitle !== titleValue) {
        handleSubmit(e);
      }
    },
    [handleDeleteSubTask, handleSubmit, taskTitle, titleValue],
  );

  const onTitleEditorKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!titleValue.trim()) {
          return;
        }
        onTitleEditorBlur(e);
        openAddSubtask?.();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onTitleEditorBlur, titleValue],
  );

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isCalendar && refr.current && !refr.current.contains(e.target)) {
        setIsCalendar(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isCalendar]);

  useEffect(() => {
    setCompleted(isCompleted);
  }, [isCompleted]);

  useEffect(() => {
    setTitleValue(taskTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskTitle]);

  return !titleIdentifier ? <SubtaskPlaceholder /> : (
    <TaskCardWithDropdown taskId={subTaskId} isSubtask titleIdentifier={titleIdentifier} tasks={tasks}>
      <div
        className={`subtask-row-wrapper w-full min-h-8 flex align-center cursor-pointer hover:bg-slate-50 ${compressed ? 'compressed' : ''}`}
        onClick={openTicket}
      >
        <div
          onClick={handleComplete}
          className={`complete-task-icon ${compressed ? 'size-5' : 'size-6'} ml-1 rounded-full flex align-center justify-center items-center cursor-pointer ${completed ? 'completed bg-green-600' : 'bg-white border border-gray-400'} hover:opacity-50`}
        >
          <svg
            className={`${compressed ? 'size-2' : 'size-2.5'} fill-current ${completed ? 'text-white' : 'text-slate-900'} `}
            viewBox="0 0 12 12"
          >
            <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
          </svg>
        </div>
        <div className={`ml-2 w-full flex align-center justify-between overflow-hidden`}>
          <div className="subtask-title-textarea-wrapper-container">
            <div className="subtask-title-textarea-wrapper">
              <div className={`subtask-title-textarea-shadow ${compressed ? 'text-xs' : 'text-sm'}`}>
                {titleValue}
              </div>
              <textarea
                className={`subtask-title-textarea ${compressed ? 'text-xs' : 'text-sm'} cursor-text `}
                id={titleId}
                onBlur={onTitleEditorBlur}
                onChange={onTitleEditorChange}
                onKeyDown={onTitleEditorKeyDown}
                type="text"
                value={titleValue}
              />
            </div>
          </div>
          {subTaskOrder?.length > 0 && (
            <div className="flex align-start mr-2 multilevel-subtasks-indication">
              <p className="text-xs">{subTaskOrder?.length}</p>
              <img className="size-4 ml-1" src="/images/icons/subtask-arrow.svg" alt="Search Icon" />
            </div>
          )}
          <div className={`subtask-right-side ${isFocused ? 'active' : ''}`}>
            {!compressed && (
              <div className="flex align-center justify-end">
                <div className="mx-2">
                  <ConnectedPriorityDropdown taskId={subTaskId} showLabel={false} />
                </div>
                <div className="mr-2">
                  <ConnectedAssigneeSelect iconSize={24} taskId={subTaskId} count={1} onClose={setUnfocused} onOpen={setFocused} />
                </div>
                <div onClick={(e) => e.stopPropagation()} className="w-16">
                  <TaskCalendarDropdownWrapper taskId={subTaskId} format="MMM DD" showIcon={false} onClose={setUnfocused} onOpen={setFocused} />
                </div>
                <Tooltip title="Subtask details">
                  <div className="show-subtask-details-btn" onClick={openTicket}>
                    <KeyboardArrowRightIcon fontSize="small" />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>
    </TaskCardWithDropdown>
  );
};

const makeMapStateToProps = () => {
  const getTaskParentId = makeGetTaskParentId();
  const getTaskTitle = makeGetTaskTitle();
  const getTaskTitleIdentifier = makeGetTaskTitleIdentifier();
  const getSubTaskOrder = makeGetSubTaskOrder();
  const isTaskCompletedSelector = makeIsTaskCompletedSelector();

  return (state, ownProps) => ({
    isCompleted: isTaskCompletedSelector(state, { taskId: ownProps?.subTaskId }),
    parentTaskId: getTaskParentId(state, { taskId: ownProps?.subTaskId }),
    subTaskOrder: getSubTaskOrder(state, { taskId: ownProps?.subTaskId }),
    taskTitle: getTaskTitle(state, { taskId: ownProps?.subTaskId }),
    titleIdentifier: getTaskTitleIdentifier(state, { taskId: ownProps?.subTaskId }),
    tasks: state.tasks?.entities
  });
};

export default connect(makeMapStateToProps)(memo(SubTask));
